import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BookOpen, Search, Lightbulb, MessageSquareWarning, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Read-only talking-point library — pulls together the `hints` and
 * `objection_rebuttals` fields from every active script in
 * `telemarketing_scripts` and surfaces them as a searchable reference card.
 *
 * Designed for telemarketers to keep open in a second tab while they're on a
 * call — fast keyword search, big readable text, no edit UI to get in the way.
 */
export default function TelemarketingHintsView() {
  const { api } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/telemarketing/scripts');
        setScripts(data || []);
        if ((data || [])[0]?._id) setOpenId(data[0]._id);
      } catch { setScripts([]); }
      setLoading(false);
    })();
  }, [api]);

  // Index every hint + rebuttal across all scripts. Allows global keyword
  // search (e.g. "price", "competitor", "warranty") to surface every entry.
  const allRebuttals = useMemo(() => {
    const out = [];
    scripts.forEach(s => {
      (s.objection_rebuttals || []).forEach(o => {
        out.push({ scriptId: s._id, scriptTitle: s.title, scriptType: s.script_type, objection: o.objection || '', rebuttal: o.rebuttal || '' });
      });
    });
    return out;
  }, [scripts]);

  const filteredRebuttals = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRebuttals;
    return allRebuttals.filter(r => (
      r.objection.toLowerCase().includes(q) ||
      r.rebuttal.toLowerCase().includes(q) ||
      r.scriptTitle.toLowerCase().includes(q)
    ));
  }, [allRebuttals, search]);

  const filteredHints = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scripts.filter(s => s.hints && (
      !q ||
      (s.hints || '').toLowerCase().includes(q) ||
      (s.title || '').toLowerCase().includes(q)
    ));
  }, [scripts, search]);

  if (loading) {
    return <div data-testid="tm-hints-loading" className="bg-white border border-slate-200 rounded-xl p-6 text-center text-slate-400 text-sm flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading talking points…</div>;
  }

  return (
    <div data-testid="tm-hints-view" className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
        <BookOpen size={14} className="text-[#D35400]" />
        <h2 className="text-sm font-bold text-slate-800">Talking-point library</h2>
        <span className="text-[10px] text-slate-400 hidden sm:inline">Keep this open during calls — search any keyword (price, warranty, competitor…)</span>
        <div className="ml-auto relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="tm-hints-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search hints + rebuttals…"
            className="pl-7 pr-3 py-1.5 border border-slate-200 rounded-md text-xs w-64 focus:border-[#D35400] focus:outline-none"
          />
        </div>
      </div>

      {/* Tips per script */}
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <header className="px-4 py-2.5 border-b border-slate-100 bg-amber-50/30 flex items-center gap-2">
          <Lightbulb size={13} className="text-amber-600" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-amber-800">Tips & hints by script</h3>
          <span className="text-[10px] text-amber-600 ml-auto">{filteredHints.length} script{filteredHints.length !== 1 ? 's' : ''}</span>
        </header>
        {filteredHints.length === 0 ? (
          <p className="px-4 py-6 text-center text-xs text-slate-400">No tips match your search.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filteredHints.map(s => {
              const open = openId === s._id;
              return (
                <li key={s._id}>
                  <button
                    data-testid={`tm-hints-script-${s._id}`}
                    onClick={() => setOpenId(open ? null : s._id)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-xs hover:bg-slate-50"
                  >
                    <span className="font-bold text-slate-700">{s.title}</span>
                    {s.script_type && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">{s.script_type}</span>}
                    <span className="ml-auto text-slate-400">{open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}</span>
                  </button>
                  {open && (
                    <div className="px-4 pb-3 -mt-1 text-xs text-amber-800 bg-amber-50/30 leading-relaxed whitespace-pre-line">
                      {s.hints}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Objection rebuttals — global searchable list */}
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <header className="px-4 py-2.5 border-b border-slate-100 bg-rose-50/30 flex items-center gap-2">
          <MessageSquareWarning size={13} className="text-rose-600" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-rose-800">Objection rebuttals</h3>
          <span className="text-[10px] text-rose-600 ml-auto">{filteredRebuttals.length} rebuttal{filteredRebuttals.length !== 1 ? 's' : ''}</span>
        </header>
        {filteredRebuttals.length === 0 ? (
          <p className="px-4 py-6 text-center text-xs text-slate-400">No rebuttals match your search.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredRebuttals.map((r, i) => (
              <article key={`${r.scriptId}-${i}`} data-testid={`tm-hints-rebuttal-${i}`} className="px-4 py-3">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">{r.scriptTitle}</span>
                  {r.scriptType && <span className="text-[10px] text-slate-400">{r.scriptType}</span>}
                </div>
                <p className="text-xs font-bold text-rose-700 mb-1">“{r.objection}”</p>
                <p className="text-xs text-slate-700 whitespace-pre-line leading-relaxed">{r.rebuttal}</p>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
